import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorMessage, isErrorCode } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { InfoSectionContainer } from '@/common/InfoSection';
import { WBSButton } from '@/common/WBSButton';
import { Avatar } from '@/header/Avatar';
import { useCurrentUser } from '~/hooks/useCurrentUser';

interface StudyHelperInfoProps {
  studentId: number;
}

export function StudyHelperInfo({ studentId }: StudyHelperInfoProps) {
  const { t } = useTranslation();
  const { me } = useCurrentUser();
  const {
    data: helper,
    error,
    isLoading,
  } = api.getStudentStudyHelper.useQuery(
    { path: { student_id: studentId } },
    // No helper assigned is a real answer, so a 404 isn't retried; anything
    // else gets the usual three retries.
    {
      retry: (failureCount, err) =>
        failureCount < 3 && !isErrorCode(err, 'resource_not_found'),
    },
  );

  if (isLoading) {
    return null;
  }

  // Only a 404 means no helper is assigned; other errors mustn't be shown as
  // that.
  if (error && !isErrorCode(error, 'resource_not_found')) {
    return (
      <StudyHelperInfoContainer>
        <AlertBox variant="error">
          {errorMessage(error, t('wbs.something-went-wrong-please'))}
        </AlertBox>
      </StudyHelperInfoContainer>
    );
  }

  if (!helper) {
    return (
      <StudyHelperInfoContainer>
        <DetailValue>{t('lessons.awaiting-adoption-by-a')}</DetailValue>
      </StudyHelperInfoContainer>
    );
  }

  const genderLabel =
    helper.gender === 'male'
      ? t('wbs.male')
      : helper.gender === 'female'
        ? t('wbs.female')
        : t('wbs.n-a-2');

  return (
    <StudyHelperInfoContainer>
      {/* Legacy links the avatar to the helper's profile; it isn't linked
         here. The member profile route now resolves for non-students too,
         so this could link as `AvatarNameCell` does. */}
      <Avatar size={200} src={helper.avatar || null} />
      <StudyHelperDetails>
        <DetailRow>
          <DetailLabel>{t('wbs.name')}:</DetailLabel>
          <DetailValue>{helper.display_name}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{t('wbs.country')}:</DetailLabel>
          <DetailValue>{helper.country || t('wbs.n-a-2')}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{t('wbs.gender')}:</DetailLabel>
          <DetailValue>{genderLabel}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{t('wbs.age')}:</DetailLabel>
          <DetailValue>{helper.age ?? t('wbs.n-a-2')}</DetailValue>
        </DetailRow>
        {me?.id ? (
          <WBSButton
            style={{ marginTop: '23px' }}
            as={Link}
            to="/members/$memberId/messages/compose/"
            // The viewer's own member id, not the student's: compose lives
            // under the viewer's messages.
            params={{ memberId: String(me.id) }}
            // Opens compose addressed to the helper. A study helper's id is
            // their user id, which `createThread` takes as `recipient_id`.
            search={{ r: helper.id }}
            wide
          >
            {t('lessons.message-study-helper')}
          </WBSButton>
        ) : null}
      </StudyHelperDetails>
    </StudyHelperInfoContainer>
  );
}

const StudyHelperInfoContainer = styled(InfoSectionContainer)`
  align-items: center;
  display: flex;
  gap: 40px;
  margin-bottom: 45px;
  padding: 30px 30px 39px 40px;
`;

const StudyHelperDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 11px;
`;

const DetailRow = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 8px;
`;

const DetailLabel = styled.span`
  color: var(--black);
  font-family: var(--raleway);
  font-size: 15px;
  font-weight: 600;
`;

const DetailValue = styled.span`
  color: var(--black);
  font-family: var(--raleway);
  font-size: 13px;
  font-weight: 400;
`;
